<script lang="ts">
import { computed, defineComponent, inject, onActivated, onMounted, PropType, ref, watch } from "vue";
import formatUtils from "@/businesslogic/utils/formatUtils";
import Steps from "@/components/Steps.vue";
import TarifauswahlInformationenVGV from "@/components/tarif/TarifauswahlInformationenVGV";
import Tarifauswahl from "@/components/tarif/Tarifauswahl.vue";
import Leistungstabelle from "@/components/leistungstabelle/Leistungstabelle.vue";
import LeistungenVGVDirekt from "@/components/leistungstabelle/LeistungenVGVDirekt";
import LeistungenVGVMakler from "@/components/leistungstabelle/LeistungenVGVMakler";
import Tarifoptionen from "@/components/tarifoptionen/Tarifoptionen.vue";
import TarifoptionInformationenVGV from "@/components/tarifoptionen/TarifoptionInformationenVGV";
import Produktmodul from "@/components/module/Produktmodul.vue";
import ProduktmodulAusschluss from "@/components/module/ProduktmodulAusschluss.vue";
import AusschlussmodulElementar from "@/components/module/ausschlussmodule/ausschlussmodulElementar";
import ProduktmoduleVGV from "@/components/module/produktmoduleVGV";
import Beitrag from "@/components/Beitrag.vue";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import formValidierung from "@/businesslogic/formValidierung";
import EmailOderHerunterladen from "@/components/vorschlag/EmailOderHerunterladen.vue";
import Maklerrabatt from "@/components/maklerrabatt/Maklerrabatt.vue";
import { NavvItem } from "@/model/NavItem";
import Logger from "js-logger";
import storeUtils from "@/businesslogic/utils/storeUtils";
import dataUtils from "@/businesslogic/utils/dataUtils";
import HugRabatt from "@/components/HugRabatt.vue";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "VergleichVGV",
  emits: ["prev-page", "next-page", "goto-page", "handle-error"],
  components: {
    HugRabatt,
    EmailOderHerunterladen,
    Steps,
    Tarifauswahl,
    Leistungstabelle,
    Tarifoptionen,
    Produktmodul,
    ProduktmodulAusschluss,
    Beitrag,
    ValidierungsFehler,
    Maklerrabatt,
  },
  props: {
    isMakler: Boolean,
    navItems: Object as PropType<NavvItem[]>,
  },
  setup(props, { emit }) {
    const piniaStore = useFormStore();
    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");
    const tarifauswahlInformationen = ref(TarifauswahlInformationenVGV);
    const leistungen = ref(getLeistung());
    const tarifoptionInformationen = ref(TarifoptionInformationenVGV);
    const produktmodule = ref(ProduktmoduleVGV);
    const ausschlussModul = ref(AusschlussmodulElementar);
    const { formatCurrency } = formatUtils();
    const { istPhotovoltaikVersicherungMoeglich } = storeUtils();
    const { gte, lte } = dataUtils();

    const aktiveLinie = ref(1);
    const { validiereForm, getFormFehlerAnzahl, getFormFehler, validiereRabatt, validiereVorschaeden } =
      formValidierung();
    let validiert = false;

    const fehler = ref({} as any);

    const ueberschwemmungText = computed(() => {
      return piniaStore.state.zuersZone.zone === "4"
        ? "Rückstau und Überschwemmung sind nicht versichert"
        : "Überschwemmung aufgrund Starkregen";
    });

    function getLeistung() {
      if (piniaStore.state.isMakler) {
        return LeistungenVGVMakler;
      }
      return LeistungenVGVDirekt;
    }

    function istGewaehlteProduktLinie(produktLinie: string) {
      return piniaStore.state.tarifinformationen.produktLinie === produktLinie;
    }

    function wechsleTarif() {
      if (piniaStore.state.tarifinformationen.produktLinie === "001") {
        aktiveLinie.value = 1;
      } else if (piniaStore.state.tarifinformationen.produktLinie === "002") {
        aktiveLinie.value = 2;
      } else if (piniaStore.state.tarifinformationen.produktLinie === "003") {
        aktiveLinie.value = 3;
      } else {
        Logger.debug("wechsleTarif(): Unbekannte Produktlinie:" + piniaStore.state.tarifinformationen.produktLinie);
      }
      validiereRabatt(piniaStore.state, fehler, validiert);
    }

    function eingabenSindValide() {
      return new Promise((resolve) => {
        validiereForm("phForm").then(() => {
          Logger.debug("Anzahl Fehler aus Validierung: " + getFormFehlerAnzahl());
          fehler.value = getFormFehler();

          if (piniaStore.state.tarifinformationen.sfr != 0) {
            validiereVorschaeden(piniaStore.state, fehler, validiert);
          }

          validiert = true;

          validiereRabatt(piniaStore.state, fehler, validiert);

          Logger.debug("Fehler-Objekt: ", fehler.value);
          resolve(getFormFehlerAnzahl() === 0);
        });
      });
    }

    function getTIB(): string {
      if (piniaStore.state.isMakler) {
        return "https://www.gev-versicherung.de/downloads/pdf/TI-TIB-VG-ProtectPlus-2023.pdf";
      }
      return "https://www.gev-versicherung.de/downloads/pdf/TI-TIB-VG-Home-2023.pdf";
    }

    function sollAusschlussZeigen() {
      const modul = produktmodule.value.find((i) => i.modulId === "elementar");
      if (modul) {
        return (
          !modul.anzeigen &&
          !piniaStore.state.gebaeudedaten.rueckstausicherungVorhanden &&
          piniaStore.state.zuersZone.starkregen === "3"
        );
      }
      return false;
    }

    function modulAnzeige(modulId: string, schalter: boolean): void {
      const modul = produktmodule.value.find((i) => i.modulId === modulId);
      if (modul) modul.anzeigen = schalter;
    }

    function checkAnzuzeigendeModule(): void {
      checkAnzeigeElementarModul();
      checkAnzeigeFeuerrohbauModul();
      checkAnzeigePhotovoltaikModul();
    }

    function checkAnzeigeElementarModul() {
      if (
        istBAKV() ||
        piniaStore.state.gebaeudedaten.gebaeudenutzung === "0004" ||
        (!piniaStore.state.isMakler && piniaStore.state.zuersZone.zone === "4") ||
        (!piniaStore.state.gebaeudedaten.rueckstausicherungVorhanden && piniaStore.state.zuersZone.starkregen === "3")
      ) {
        modulAnzeige("elementar", false);
        piniaStore.state.tarifinformationen.elementar = false;
      } else {
        modulAnzeige("elementar", true);
      }
    }

    function checkAnzeigeFeuerrohbauModul() {
      const currentDate = new Date();
      const baujahr = piniaStore.state.gebaeudedaten.baujahr;
      const minJahr = currentDate.getFullYear();
      const maxJahr = minJahr + 1;
      if (gte(baujahr, minJahr) && lte(baujahr, maxJahr)) {
        modulAnzeige("rohbauversicherung", true);
        if (piniaStore.state.isMakler) {
          feuerRohbauMakler();
        }
      } else {
        modulAnzeige("rohbauversicherung", false);
        piniaStore.state.tarifinformationen.rohbauversicherung = false;
      }
    }

    function feuerRohbauMakler() {
      const modul = produktmodule.value.find((i) => i.modulId === "rohbauversicherung");

      if (modul) {
        if (
          piniaStore.state.tarifinformationen.produktLinie === "002" ||
          piniaStore.state.tarifinformationen.produktLinie == "003"
        ) {
          modul.nameMakler = "Rohbauversicherung";
          modul.body =
            '<div class="o-grid__cell o-grid__cell--9">' +
            '<ul class="c-list">' +
            '<li class="c-list__item">Versicherte Gefahren während der Rohbauphase: Feuer, Sturm/Hagel, Leitungswasser</li>' +
            '<li class="c-list__item">Laufzeit bis zu 24 Monate</li>' +
            "</ul>" +
            "</div>";
        } else {
          modul.nameMakler = "Feuerrohbauversicherung";
          modul.body =
            '<div class="o-grid__cell o-grid__cell--9">' +
            '<ul class="c-list">' +
            '<li class="c-list__item">Versicherte Gefahren während der Rohbauphase: Feuer</li>' +
            '<li class="c-list__item">Laufzeit bis zu 24 Monate</li>' +
            "</ul>" +
            "</div>";
        }
      }
    }

    function checkAnzeigePhotovoltaikModul() {
      if (
        piniaStore.state.gebaeudedaten.photovoltaikVorhanden &&
        istPhotovoltaikVersicherungMoeglich(piniaStore.state)
      ) {
        modulAnzeige("photovoltaikModul", true);
      } else {
        modulAnzeige("photovoltaikModul", false);
        piniaStore.state.tarifinformationen.photovoltaikModul = false;
      }
    }

    function istBAKV() {
      return piniaStore.state.gebaeudedaten.bauart === "0003" && piniaStore.state.gebaeudedaten.dachbelag === "1";
    }

    function nextPage() {
      eingabenSindValide().then((isValid) => {
        if (isValid) {
          emit("next-page", { pageIndex: 2 });
        } else {
          document.querySelector("div#tariffOptionsTable")?.scrollIntoView({ behavior: "smooth" });
        }
      });
    }

    watch(
      () => piniaStore.state.tarifinformationen.abschlagZuschlagHoehe,
      () => {
        validiert = true;
        validiereRabatt(piniaStore.state, fehler, validiert);
      },
    );

    watch(
      () => piniaStore.state.tarifinformationen.vorschaedenAnzahl,
      (wertNeu) => {
        if (wertNeu != -1) {
          fehler.value["vorschaedenAnzahl"] = null;
        }
      },
    );

    watch(
      () => piniaStore.state.tarifinformationen.produktLinie,
      (tarifNeu, tarifAlt) => {
        if (tarifNeu !== tarifAlt) {
          wechsleTarif();
          checkAnzuzeigendeModule();
        }
      },
    );

    watch(
      () => [
        piniaStore.state.gebaeudedaten.vermietet,
        piniaStore.state.gebaeudedaten.baujahr,
        piniaStore.state.gebaeudedaten.bauart,
        piniaStore.state.gebaeudedaten.dachbelag,
      ],
      () => {
        checkAnzuzeigendeModule();
      },
    );

    watch(
      () => piniaStore.state.tarifinformationen.sfr,
      (wertNeu) => {
        if (wertNeu == 0) {
          piniaStore.state.tarifOptionenZuVerbergen.push("anzahlvorschaeden");
        } else {
          piniaStore.state.tarifOptionenZuVerbergen = [];
        }
      },
    );

    onMounted(() => {
      wechsleTarif();
      checkAnzuzeigendeModule();
    });

    onActivated(() => {
      checkAnzuzeigendeModule();
    });

    return {
      istGewaehlteProduktLinie,
      getTIB,
      nextPage,
      props,
      piniaStore,
      wechsleSichtbarkeit,
      istSichtbar,
      fehler,
      formatCurrency,
      tarifauswahlInformationen,
      leistungen,
      tarifoptionInformationen,
      produktmodule,
      sollAusschlussZeigen,
      ausschlussModul,
      aktiveLinie,
      ueberschwemmungText,
    };
  },
});
</script>

<template>
  <Steps :active="2" v-bind:items="navItems" @goto-page="$emit('goto-page', $event)"></Steps>
  <section class="js-step-3">
    <tarifauswahl :tarifauswahlInformationen="tarifauswahlInformationen" />
    <leistungstabelle :aktiveLinie="aktiveLinie" :inhalt="leistungen" :link="getTIB()" />
    <tarifoptionen :tarifoptionInformationen="tarifoptionInformationen" />
    <div v-if="piniaStore.state.isMakler && piniaStore.state.tarifinformationen.sfr === 0" class="o-grid">
      <div
        class="o-grid__cell o-grid__cell--12 o-grid__cell--9-480 o-grid__cell--8-768 o-grid__cell--7-1024 o-grid__cell--6-1200 o-grid__cell--offset-3 notice-box"
      >
        <strong>Hinweis:</strong> Das SFR-Modell ist obligatorisch, wenn mehr als 1 Vorschaden und eine Schadenquote >60
        % dem Antrag zu Grunde liegen
      </div>
    </div>
    <div v-if="fehler.vorschaedenAnzahl" class="o-grid">
      <div
        class="o-grid__cell o-grid__cell--12 o-grid__cell--9-480 o-grid__cell--8-768 o-grid__cell--7-1024 o-grid__cell--6-1200 o-grid__cell--offset-3-480"
      >
        <div class="o-grid o-grid--no-y-gutter o-grid--align-flex-end">
          <div class="o-grid__cell o-grid__cell--12">
            <validierungs-fehler :fehler-text="fehler.vorschaedenAnzahl" />
          </div>
        </div>
      </div>
    </div>
    <produktmodul-ausschluss v-if="sollAusschlussZeigen()" :produktmodul-auschluss="ausschlussModul" />
    <produktmodul :produktmodule="produktmodule" :zeige-icons="true">
      <template v-slot:unbekannteZuersZoneHinweis>
        <div class="o-grid__cell o-grid__cell--9">
          <ul class="c-list">
            <li class="c-list__item c-list__item_warning">
              Der Beitrag kann ggf. steigen, da keine ZÜRS-Zone ermittelt werden konnte.
            </li>
          </ul>
        </div>
      </template>
      <template v-slot:vhvElementarHinweis>
        <div class="o-grid__cell o-grid__cell--9">
          <ul class="c-list">
            <li :class="{ 'c-list--inactive': piniaStore.state.zuersZone.zone === '4' }" class="c-list__item">
              {{ ueberschwemmungText }}
            </li>
            <li class="c-list__item">Erdsenkung, Erdrutsch</li>
          </ul>
        </div>
      </template>
    </produktmodul>
    <div class="o-grid o-grid--align-middle u-mar-t-2">
      <div class="o-grid__cell o-grid__cell--12 o-grid__cell--3-480 u-text-align-right--480 u-form-grid-nospace-y">
        <h2 class="u-form-heading unset-text-align">Rabatt</h2>
      </div>
      <hug-rabatt />
    </div>

    <maklerrabatt :fehler="fehler" />
    <beitrag @next-page="nextPage" />
    <email-oder-herunterladen produkt="vgv" tracking-zusatz="vergleich" />
  </section>
</template>
